<script setup lang="ts">
import { Head, Link, router } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Tag from '@/Components/Utils/Tag.vue'
import Icon from '@/Components/Utils/Icon.vue'
import Button from '@/Components/Utils/Button.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { useFormatTime } from '@/Composables/useFormatTime'
import { notify } from '@kyvg/vue3-notification'
import { ref } from 'vue'

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    data: {
        id: number
        slug: string
        reference: string
        status: string
        status_label: string
        status_icon: { icon: string, class: string, tooltip: string }
        is_pending: boolean
        order: { id: number, slug: string, reference: string }
        product: { id: number, name: string, code: string, type: string } | null
        customer: { id: number, name: string, slug: string } | null
        created_by: { id: number, name: string }
        responded_by: { id: number, name: string } | null
        quantity: number
        note: string | null
        operator_note: string | null
        responded_at: string | null
        created_at: string
    }
}>()

const isRejectModalOpen = ref(false)
const isAcceptModalOpen = ref(false)
const operatorNoteReject = ref('')
const operatorNoteAccept = ref('')

const isLoadingAccept = ref(false)
const submitAccept = () => {
    router.patch(route('manufacture.complaints.accept', props.data.id), {
        operator_note: operatorNoteAccept.value || null,
    }, {
        onStart: () => {
            isLoadingAccept.value = true
        },
        onFinish: () => {
            isLoadingAccept.value = false
        },
        onSuccess: () => {
            isAcceptModalOpen.value = false
            notify({ title: 'Berhasil', text: 'Komplain diterima!', type: 'success' })
        },
    })
}

const isLoadingReject = ref(false)
const submitReject = () => {
    router.patch(route('manufacture.complaints.reject', props.data.id), {
        operator_note: operatorNoteReject.value,
    }, {
        onStart: () => {
            isLoadingReject.value = true
        },
        onFinish: () => {
            isLoadingReject.value = false
        },
        onSuccess: () => {
            isRejectModalOpen.value = false
            notify({ title: 'Berhasil', text: 'Komplain ditolak.', type: 'success' })
        },
    })
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #button-delete-complaint="{ action }">
            <ModalConfirmationDelete
                :routeDelete="action.route"
                :title="'Yakin ingin menghapus komplain ' + data.reference + '?'"
                description="Komplain akan dihapus permanen."
                isFullLoading
                @success="() => notify({ title: 'Berhasil', text: 'Komplain berhasil dihapus!', type: 'success' })"
            >
                <template #default="{ changeModel }">
                    <Button
                        @click="changeModel()"
                        :style="action.style"
                        :label="action.label"
                        :icon="action.icon"
                        :key="`ActionButton${action.label}${action.style}`"
                        :tooltip="action.tooltip"
                    />
                </template>
            </ModalConfirmationDelete>
        </template>

        <template #button-reject-complaint="{ action }">
            <Button
                @click="isRejectModalOpen = true"
                :style="action.style"
                :label="action.label"
                :icon="action.icon"
            />
        </template>

        <template #button-accept-complaint="{ action }">
            <Button
                @click="isAcceptModalOpen = true"
                :style="action.style"
                :label="action.label"
                :icon="action.icon"
            />
        </template>
    </PageHeading>

    <div class="mx-8 max-w-2xl mt-6 space-y-6">
        <!-- Status badge -->
        <div class="flex items-center gap-x-2">
            <Icon :data="data.status_icon" />
            <span class="font-medium text-sm">{{ data.status_label }}</span>
        </div>

        <!-- Detail card -->
        <div class="w-full overflow-hidden rounded-xl border border-sky-400">
            <div class="flex flex-col gap-y-1 border-b border-gray-900/5 bg-gray-50 p-6">
                <div class="font-bold text-lg">{{ data.reference }}</div>
                <div v-tooltip="'Tgl. dibuat'" class="text-sm text-gray-500 w-fit">
                    {{ useFormatTime(data.created_at) }}
                </div>
            </div>

            <dl class="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6">
                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">No. Order</dt>
                    <dd>
                        <Link :href="route('casheer.orders.show', data.order.slug)" class="primaryLink font-medium">
                            {{ data.order.reference }}
                        </Link>
                    </dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Produk</dt>
                    <dd class="font-medium">{{ data.product?.name ?? '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Pelanggan</dt>
                    <dd class="font-medium">{{ data.customer?.name ?? '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Jumlah Komplain</dt>
                    <dd class="font-medium">{{ data.quantity }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Keterangan</dt>
                    <dd class="font-medium">{{ data.note ?? '-' }}</dd>
                </div>

                <div class="flex justify-between gap-x-4 py-3">
                    <dt class="text-gray-500">Dibuat oleh</dt>
                    <dd class="font-medium">{{ data.created_by?.name ?? '-' }}</dd>
                </div>

                <template v-if="!data.is_pending">
                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Direspon oleh</dt>
                        <dd class="font-medium">{{ data.responded_by?.name ?? '-' }}</dd>
                    </div>

                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Catatan Operator</dt>
                        <dd class="font-medium">{{ data.operator_note ?? '-' }}</dd>
                    </div>

                    <div class="flex justify-between gap-x-4 py-3">
                        <dt class="text-gray-500">Tgl. Respon</dt>
                        <dd class="font-medium">{{ data.responded_at ? useFormatTime(data.responded_at) : '-' }}</dd>
                    </div>
                </template>
            </dl>
        </div>
    </div>

    <!-- Modal: Tolak Komplain -->
    <Teleport to="body">
        <div v-if="isRejectModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div class="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 space-y-4">
                <h3 class="font-bold text-lg">Tolak Komplain</h3>
                <p class="text-sm text-gray-500">Berikan alasan penolakan yang jelas.</p>
                <textarea
                    v-model="operatorNoteReject"
                    rows="4"
                    placeholder="Alasan penolakan..."
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
                <div class="flex justify-end gap-x-3">
                    <Button style="secondary" label="Batal" @click="isRejectModalOpen = false" />
                    <Button style="delete" label="Tolak Komplain" icon="fal fa-times" @click="submitReject" :loading="isLoadingReject" />
                </div>
            </div>
        </div>
    </Teleport>

    <!-- Modal: Terima Komplain -->
    <Teleport to="body">
        <div v-if="isAcceptModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div class="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4 space-y-4">
                <h3 class="font-bold text-lg">Terima Komplain</h3>
                <p class="text-sm text-gray-500">
                    Stok produk akan berkurang sebanyak <strong>{{ data.quantity }}</strong> unit.
                    Tidak mempengaruhi invoice.
                </p>
                <textarea
                    v-model="operatorNoteAccept"
                    rows="3"
                    placeholder="Catatan (opsional)..."
                    class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
                <div class="flex justify-end gap-x-3">
                    <Button type="tertiary" label="Batal" @click="isAcceptModalOpen = false" />
                    <Button type="save" full label="Terima Komplain" icon="fal fa-check" @click="submitAccept" :loading="isLoadingAccept" />
                </div>
            </div>
        </div>
    </Teleport>
</template>
